import { HttpHeaders } from "@angular/common/http";
import { defer, from, Observable } from "rxjs";
import { switchMap } from "rxjs/operators";

import { ApiService } from "@bitwarden/common/abstractions/api.service";

/**
 * Upper bound on a gloss-grocery request. Without it a hung request never
 * settles, so a caller's `firstValueFrom` never resolves and any refresh/save
 * spinner is stuck on forever. On timeout the call errors, which callers fold
 * into their own "couldn't reach the server" fallback.
 */
export const GROCERY_TIMEOUT_MS = 8000;

/**
 * Wraps a gloss-grocery request with the caller's gloss-api bearer token.
 *
 * The token is read inside `defer`, so it is fetched on *subscribe* rather than
 * eagerly when the Observable is built — a resubscribe or `retry` then reads a
 * fresh token instead of replaying the one captured at call time. Shared by the
 * catalog ({@link GroceryService}) and shopping-list ({@link ShoppingListService})
 * clients so the token/header wiring lives in exactly one place.
 */
export function withGroceryAuth<T>(
  apiService: ApiService,
  call: (headers: HttpHeaders) => Observable<T>,
): Observable<T> {
  return defer(() => from(apiService.getActiveBearerToken())).pipe(
    switchMap((token) => call(new HttpHeaders({ Authorization: `Bearer ${token}` }))),
  );
}
